import { useState, useEffect, useRef, useMemo } from 'react';
import { useFollowedState } from '../live/live';
import { Calendar, Video, MapPin, FileText, Plus, X, ChevronDown, RefreshCw, Mic, Trash2, MessageSquare, Reply, Pencil, Check, Paperclip, Link2, Unlink2, RotateCcw, Flag, Users, ListTodo, Milestone, FileAudio } from 'lucide-react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import { useAuth } from '../context/AuthContext';
import { ConfirmModal } from './ConfirmModal';
import { TranscriptModal } from './TranscriptModal';
import { RedoTranscriptButton } from './RedoTranscript';
import { TaskTimeline } from './TaskTimeline';
import { AutoTextarea, InlineReplyForm } from './InlineReplyForm';
import { AttachmentList } from './AttachmentWidgets';
import { AudioImportDialog } from './AudioImportDialog';
import { AUDIO_ACCEPT, isAudioFile } from '../utils/audio';
import { docApi } from '../proposals/documents/api';
import { useT } from '../i18n';
import { vjtApi } from '../proposals/vue-jalons-taches/api';
import type { CalendarEvent, Transcription, ProjectTimeline as ProjectTimelineData, ProjectEvent, MeetingAttendee, TimelineEntry, TaskUpdate, TaggableUser, Attachment } from '../types';
import { taskStatusName } from '../utils/taskStatusName';

type TFunc = (key: string, params?: Record<string, string | number>) => string;

const MIN_COLLAPSED_HEIGHT = 700;

// ── Helpers ──

function parseAttendees(json: any): MeetingAttendee[] {
  if (!json) return [];
  if (typeof json === 'object' && Array.isArray(json)) return json;
  try { return JSON.parse(json); } catch { return []; }
}

function formatCompact(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' ' +
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ── Activity entries: one icon per kind of change ──
export type ActivityKind = 'stage' | 'edit' | 'team' | 'task' | 'doc' | 'milestone';
export function activityKind(type: string): ActivityKind {
  if (type === 'status_changed' || type === 'project_closed' || type === 'project_created') return 'stage';
  if (type === 'sponsor_changed' || type.startsWith('collaborator_') || type === 'allocation_changed' || type === 'project_shared') return 'team';
  if (type.startsWith('document_')) return 'doc';
  if (type.startsWith('milestone_')) return 'milestone';
  if (type.startsWith('task_')) return 'task';
  return 'edit';
}
export const ACTIVITY_ICON: Record<ActivityKind, typeof Flag> = {
  stage: Flag, edit: Pencil, team: Users, task: ListTodo, doc: FileText, milestone: Milestone,
};
/** "28 oct. · 10:12" in the interface language. */
function formatWhen(iso: string, lang: string) {
  const d = new Date(iso);
  const loc = lang === 'fr' ? 'fr-CH' : 'en-GB';
  return `${d.toLocaleDateString(loc, { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })}`;
}
function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';
}

function formatAttendees(attendees: MeetingAttendee[], max = 3): string | null {
  if (attendees.length === 0) return null;
  const names = attendees.map(a => {
    const parts = a.name.split(' ');
    if (parts.length >= 2) return `${parts[0][0]}. ${parts[parts.length - 1]}`;
    return a.name;
  });
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')}, +${names.length - max}`;
}

function formatDuration(seconds: number | null): string | null {
  if (!seconds) return null;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60 > 0 ? ` ${m % 60}m` : ''}`;
}

/** When a recording made or uploaded in PMO ran, and how long: 14:04–14:37 · 32min (35s when under a minute). */
function recordingSpan(tr: Transcription): string | null {
  if (!tr.source || tr.source === 'sync' || !tr.meeting_date) return null;
  const secs = tr.duration_seconds || 0;
  const end = secs ? new Date(new Date(tr.meeting_date).getTime() + secs * 1000).toISOString() : null;
  const len = !secs ? null : secs < 60 ? `${secs}s` : formatDuration(secs);
  return [end ? `${formatTime(tr.meeting_date)}–${formatTime(end)}` : formatTime(tr.meeting_date), len].filter(Boolean).join(' · ');
}

/** Speakr is still working on it (statuses as Speakr names them: PENDING, PROCESSING, SUMMARIZING…; UPLOADING: PMO is still handing it over). */
export function isTranscribing(status: string | null | undefined): boolean {
  return /^(uploading|pending|processing|summarizing|stitching|queued)$/i.test(status || '');
}

/**
 * Nothing to read. Only said of a recording made or uploaded in PMO, which is
 * marked failed only when Speakr produced no transcript; Speakr's own FAILED
 * also covers a transcript whose summary alone failed.
 */
const transcriptFailed = (tr: Transcription) => /^failed$/i.test(tr.status || '') && !!tr.source && tr.source !== 'sync';

/** Parse transcription title to extract date, project code, and clean remainder */
function parseTranscriptionTitle(title: string): { date: string | null; code: string | null; remainder: string } {
  let working = title.trim();
  let date: string | null = null;
  let code: string | null = null;

  // YYYYMMDD
  const isoCompact = working.match(/\b(\d{4})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\b/);
  if (isoCompact) {
    date = `${isoCompact[3]}.${isoCompact[2]}.${isoCompact[1]}`;
    working = working.replace(isoCompact[0], '').trim();
  }

  // DDMMYYYY
  if (!date) {
    const dmy = working.match(/\b(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(\d{4})\b/);
    if (dmy) {
      date = `${dmy[1]}.${dmy[2]}.${dmy[3]}`;
      working = working.replace(dmy[0], '').trim();
    }
  }

  // Project code: ae + digits
  const codeMatch = working.match(/\b(ae\d+)\b/i);
  if (codeMatch) {
    code = codeMatch[1].toUpperCase();
    working = working.replace(codeMatch[0], '').trim();
  }

  // Clean up leading separators/colons/dashes
  working = working.replace(/^[\s:·\-–—]+/, '').trim();

  return { date, code, remainder: working || title };
}

/** Truncate a string, preserving closing quote */
function truncTitle(title: string, max = 20): string {
  if (!title || title.length <= max) return title;
  return title.slice(0, max) + '..."';
}

/** Format a project event into a human-readable description */
/** One history entry in words (also used by the home page's « Quoi de neuf »). */
export function formatProjectEventDescription(event: ProjectEvent, t: TFunc): string {
  const d = event.details || {};
  switch (event.event_type) {
    case 'project_created':
      return d.project_code
        ? t('timeline.eventProjectCreatedCode', { code: d.project_code })
        : t('timeline.eventProjectCreated');
    case 'title_changed':
      return t('timeline.eventTitleChanged', { old: d.old_title, new: d.new_title });
    // A content field edited after its stage (the card's "Historique" shows before/after in full).
    case 'field_changed': {
      const v = String(d.new ?? '').trim();
      return t('timeline.eventFieldChanged', {
        field: t(`projectDetail.history.field.${d.field}`),
        value: v ? `« ${v.length > 60 ? `${v.slice(0, 60)}…` : v} »` : t('projectDetail.history.empty'),
      });
    }
    case 'sponsor_changed':
      return d.old_sponsor
        ? t('timeline.eventSponsorChanged', { old: d.old_sponsor, new: d.new_sponsor || t('common.none') })
        : t('timeline.eventSponsorSet', { new: d.new_sponsor });
    case 'collaborator_added':
      return d.allocation
        ? t('timeline.eventCollaboratorAddedAlloc', { name: d.collaborator, n: d.allocation })
        : t('timeline.eventCollaboratorAdded', { name: d.collaborator });
    case 'collaborator_removed':
      return t('timeline.eventCollaboratorRemoved', { name: d.collaborator });
    case 'allocation_changed':
      return t('timeline.eventAllocationChanged', { name: d.collaborator, old: d.old_allocation, new: d.new_allocation });
    case 'status_changed':
      return t('timeline.eventStatusChanged', { old: d.old_status, new: d.new_status });
    case 'project_closed':
      return t('timeline.eventProjectClosed');
    // Task events — truncate title only, keep action verb visible
    case 'task_created':
      return t('timeline.eventTaskCreated', { title: truncTitle(d.task_title) });
    case 'task_assigned':
      return t('timeline.eventTaskAssigned', { title: truncTitle(d.task_title), name: d.assignee });
    case 'task_collaborator_added':
      return t('timeline.eventTaskCollaboratorAdded', { title: truncTitle(d.task_title), name: d.collaborator });
    case 'task_deleted':
      return t('timeline.eventTaskDeleted', { title: truncTitle(d.task_title) });
    case 'task_made_private':
      return t('timeline.eventTaskMadePrivate', { title: truncTitle(d.task_title) });
    case 'task_made_public':
      return t('timeline.eventTaskMadePublic', { title: truncTitle(d.task_title) });
    case 'task_status_changed':
      return t('timeline.eventTaskStatusChanged', { title: truncTitle(d.task_title), column: taskStatusName(d.new_column, t) });
    // [Proposition: documents] A document added to / removed from a to-do item, task or milestone.
    case 'document_added':
      return t(d.document_kind === 'link' ? 'docs.eventLinkAdded' : 'docs.eventFileAdded', { name: truncTitle(d.document_name, 30), target: truncTitle(d.target_label, 30) });
    case 'document_removed':
      return t('docs.eventRemoved', { name: truncTitle(d.document_name, 30), target: truncTitle(d.target_label, 30) });
    case 'document_attached':
      return t('docs.eventAttached', { name: truncTitle(d.document_name, 30), target: truncTitle(d.target_label, 30) });
    case 'task_migrated_in':
      return t('timeline.eventTaskMigratedIn', { title: truncTitle(d.task_title), project: d.from_project });
    case 'task_migrated_out':
      return t('timeline.eventTaskMigratedOut', { title: truncTitle(d.task_title), project: d.to_project });
    case 'milestone_added':
      return t('timeline.eventMilestoneAdded', { n: d.number ?? '', name: d.milestone_name || d.milestone_id });
    // The reason, when given, is the comment posted alongside.
    case 'milestone_cancelled':
      return d.archived_tasks?.length
        ? t('timeline.eventMilestoneCancelledTasks', { name: d.milestone_name || d.milestone_id, n: d.archived_tasks.length })
        : t('timeline.eventMilestoneCancelled', { name: d.milestone_name || d.milestone_id });
    // Home page: the sponsor (or the PMO) accepted a milestone deliverable, or took that back.
    case 'deliverable_validated':
      return t('home.event.deliverableValidated', { name: truncTitle(d.deliverable_title, 40) });
    case 'deliverable_validation_removed':
      return t('home.event.deliverableUnvalidated', { name: truncTitle(d.deliverable_title, 40) });
    case 'milestone_restored':
      return d.restored_tasks?.length
        ? t('timeline.eventMilestoneRestoredTasks', { name: d.milestone_name || d.milestone_id, n: d.restored_tasks.length })
        : t('timeline.eventMilestoneRestored', { name: d.milestone_name || d.milestone_id });
    // "Partager": sent to colleagues on Slack and/or by e-mail, with an optional note.
    case 'project_shared': {
      const via = (d.channels ?? []).map((c: string) => t(c === 'slack' ? 'tasks.remind.slack' : 'tasks.remind.viaEmail')).join(', ');
      const note = String(d.note ?? '').trim();
      return t(note ? 'timeline.eventProjectSharedNote' : 'timeline.eventProjectShared', {
        names: (d.recipients ?? []).join(', '), via, note: note.length > 80 ? `${note.slice(0, 80)}…` : note,
      });
    }
    default:
      return event.event_type.replace(/_/g, ' ');
  }
}

/** Render @mentions in comment text */
function renderCommentContent(text: string) {
  const parts = text.split(/(@\w[\w\s]*?\b)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@') && part.length > 1) {
      return <span key={i} className="mention-highlight">{part}</span>;
    }
    return part;
  });
}

const TASK_EVENT_TYPES = ['task_created', 'task_assigned', 'task_collaborator_added', 'task_deleted', 'task_status_changed', 'task_made_private', 'task_made_public', 'document_added', 'document_removed', 'document_attached', 'task_migrated_in', 'task_migrated_out'];

function buildTimelineEntries(
  events: CalendarEvent[],
  transcriptions: Transcription[],
  projectEvents: ProjectEvent[],
  projectComments: TaskUpdate[]
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  const linkedTranscriptionIds = new Set<number>();

  // Build combined entries for events that have linked transcriptions
  // A meeting keeps every transcript it has: Speakr's (Teams), the recorder's, uploaded audio.
  for (const event of events) {
    const linked = transcriptions.filter(t => t.calendar_event_id === event.id).sort((a, b) => a.id - b.id);
    if (linked.length > 0) {
      linked.forEach(t => linkedTranscriptionIds.add(t.id));
      entries.push({
        type: 'combined',
        date: event.start_time,
        event,
        transcription: linked[0],
        transcriptions: linked,
      });
    } else {
      entries.push({
        type: 'event',
        date: event.start_time,
        event,
      });
    }
  }

  // Add standalone transcriptions (not linked to any event)
  for (const t of transcriptions) {
    if (!linkedTranscriptionIds.has(t.id)) {
      entries.push({
        type: 'transcription',
        date: t.meeting_date || t.created_at,
        transcription: t,
      });
    }
  }

  // Add project events (setup changes and task events)
  for (const pe of projectEvents) {
    // [Proposition: implications] role gains are for the Implications table only
    if (pe.event_type === 'project_involvement') continue;
    entries.push({
      type: TASK_EVENT_TYPES.includes(pe.event_type) ? 'task_event' : 'setup_change',
      date: pe.created_at,
      projectEvent: pe,
    });
  }

  // Add top-level project comments (not replies) into the timeline
  for (const c of projectComments) {
    if (!c.reply_to_id) {
      entries.push({
        type: 'comment',
        date: c.created_at,
        comment: c,
      });
    }
  }

  // Sort by date descending
  entries.sort((a, b) => b.date.localeCompare(a.date));
  return entries;
}

/** Get a context key for a timeline entry (for grouping reply threads) */
function getEntryContextKey(entry: TimelineEntry): { type: string; id: number } | null {
  switch (entry.type) {
    case 'event':
    case 'combined':
      return entry.event ? { type: 'calendar_event', id: entry.event.id } : null;
    case 'transcription':
      return entry.transcription ? { type: 'transcription', id: entry.transcription.id } : null;
    case 'setup_change':
    case 'task_event':
      return entry.projectEvent ? { type: 'project_event', id: entry.projectEvent.id } : null;
    default:
      return null;
  }
}

// ── Component ──

interface ProjectTimelineProps {
  projectId: number;
  /** false: no file can be attached from here (comments, meetings, transcripts) — the project side panel for the proposals, where documents are added on items, tasks, milestones or the project. */
  allowAttachments?: boolean;
  /** Tighter lines (project side panel). */
  compact?: boolean;
  /** false: earlier comments are shown read-only, with no new comment, reply or edit — on the proposals' side panel, discussion happens on tasks and to-do items. */
  allowComments?: boolean;
  /** Bumped by the page when the project changed elsewhere (e.g. a milestone cancelled in the Execution tab): reloads events and comments. */
  reloadKey?: number;
  /** Called after a change made from here that other views show (a milestone restored). */
  onChanged?: () => void;
}

export function ProjectTimeline({ projectId, allowAttachments = true, compact = false, allowComments = true, reloadKey, onChanged }: ProjectTimelineProps) {
  const { t, lang } = useT();
  const { isAdmin } = useRole();
  const { user } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [projectEvents, setProjectEvents] = useState<ProjectEvent[]>([]);
  // Milestone cancellations this viewer may undo (PMO or sponsor), and the one being confirmed.
  const [restorable, setRestorable] = useState<Set<number>>(new Set());
  const [restoreConfirm, setRestoreConfirm] = useState<ProjectEvent | null>(null);
  const [timelineMode, setTimelineMode] = useState<'full' | 'reduced'>('full');
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [collapseHeight, setCollapseHeight] = useState(MIN_COLLAPSED_HEIGHT);
  const [transcriptModal, setTranscriptModal] = useState<{ id: number; title: string } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Project-level comments (all comments across all contexts)
  const [allComments, setAllComments] = useState<TaskUpdate[]>([]);
  const [taggableUsers, setTaggableUsers] = useState<TaggableUser[]>([]);

  // Direct attachments on timeline entries (not on comments)
  const [directAttachments, setDirectAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<{ contextType: string; contextId: number } | null>(null);
  // An audio file to transcribe: from the header's "Audio" button, or a meeting's paperclip (presetEventId).
  const [audioImport, setAudioImport] = useState<{ files: File[]; presetEventId: number | null } | null>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  // Entry-level reply state (which timeline entry has its thread expanded)
  // Collaborator mode: the threads the presenter opens open on the followers' screens too.
  const [expandedThreadKeys, setExpandedThreadKeys] = useFollowedState<string[]>(`timeline:${projectId}:threads`, []);
  const expandedThreads = useMemo(() => new Set(expandedThreadKeys), [expandedThreadKeys]);
  const [replyingTo, setReplyingTo] = useState<{ contextType: string; contextId: number } | null>(null);

  // Comment editing state
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [commentDeleteConfirm, setCommentDeleteConfirm] = useState<number | null>(null);

  // A recording made in PMO being deleted (asks first)
  const [recordingDeleteConfirm, setRecordingDeleteConfirm] = useState<Transcription | null>(null);

  // Link dropdown state
  const [linkDropdown, setLinkDropdown] = useState<{ transcriptionId: number; events: { id: number; subject: string; start_time: string; end_time: string }[] } | null>(null);

  // Form state (manual meeting)
  const [formSubject, setFormSubject] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formAttendees, setFormAttendees] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  // Admin can delete all entries; non-admin can delete their own manual entries only
  const canDeleteEvent = (event: CalendarEvent) =>
    isAdmin || (event.source === 'manual' && event.created_by_user_id === user?.id);
  const canDeleteProjectEvent = (_pe: ProjectEvent) => isAdmin;

  const loadTimeline = () => {
    setLoading(true);
    (api.getProjectTimeline(projectId) as Promise<ProjectTimelineData & { projectEvents: ProjectEvent[]; restorableCancellations?: number[]; timelineMode: string }>)
      .then(data => {
        setEvents(data.events);
        setTranscriptions(data.transcriptions);
        setProjectEvents(data.projectEvents || []);
        setRestorable(new Set(data.restorableCancellations ?? []));
        setTimelineMode((data.timelineMode as 'full' | 'reduced') || 'full');
        setDenied(false);
        setLoading(false);
      })
      .catch((err) => {
        if (err.message?.includes('Not authorized')) setDenied(true);
        setLoading(false);
      });
  };

  const loadComments = () => {
    api.getProjectQuestions(projectId).then((q: any) => setAllComments(q)).catch(() => {});
    api.getProjectTaggableUsers(projectId).then((u: any) => setTaggableUsers(u)).catch(() => {});
  };

  const loadDirectAttachments = () => {
    (api.getProjectAttachments(projectId) as Promise<Attachment[]>).then(all => {
      // Keep only direct attachments (not on comments)
      setDirectAttachments(all.filter(a => !a.task_update_id));
    }).catch(() => {});
  };

  useEffect(() => {
    loadTimeline();
    loadComments();
    loadDirectAttachments();
    setShowForm(false);
    setExpanded(false);
    setExpandedThreadKeys([]);
    setReplyingTo(null);
    setEditingCommentId(null);
  }, [projectId]);

  useEffect(() => {
    if (!reloadKey) return;
    loadTimeline();
    loadComments();
    loadDirectAttachments();
  }, [reloadKey]);

  // Measure left column to derive collapse height (same as TaskTimeline)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const rightCol = el.closest('.initiative-form-split-right');
    if (!rightCol) return;
    const leftCol = rightCol.previousElementSibling as HTMLElement | null;
    if (!leftCol) return;

    const update = () => {
      setCollapseHeight(Math.max(leftCol.offsetHeight, MIN_COLLAPSED_HEIGHT));
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(leftCol);
    return () => observer.disconnect();
  }, []);

  // Check for overflow against dynamic collapse height
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > collapseHeight);
  }, [events, transcriptions, projectEvents, allComments, expanded, collapseHeight]);

  // Group comments by context
  const commentsByContext = new Map<string, TaskUpdate[]>();
  for (const c of allComments) {
    const key = c.context_type && c.context_id ? `${c.context_type}:${c.context_id}` : 'project';
    if (!commentsByContext.has(key)) commentsByContext.set(key, []);
    commentsByContext.get(key)!.push(c);
  }

  // Project-level comments (standalone, context_type = 'project' or no context)
  const projectComments = allComments.filter(c => !c.context_type || c.context_type === 'project');

  // Build a set of comment IDs that are replies to top-level project comments (for threading)
  const projectCommentReplies = new Map<number, TaskUpdate[]>();
  for (const c of projectComments) {
    if (c.reply_to_id) {
      if (!projectCommentReplies.has(c.reply_to_id)) projectCommentReplies.set(c.reply_to_id, []);
      projectCommentReplies.get(c.reply_to_id)!.push(c);
    }
  }

  const getThreadComments = (contextType: string, contextId: number): TaskUpdate[] => {
    return commentsByContext.get(`${contextType}:${contextId}`) || [];
  };

  /** Check if a given entry has any reply comments */
  const entryHasReplies = (entry: TimelineEntry): boolean => {
    const ctx = getEntryContextKey(entry);
    if (!ctx) return false;
    return getThreadComments(ctx.type, ctx.id).length > 0;
  };

  const handleDirectUpload = async (files: FileList, contextType: string, contextId: number) => {
    if (files.length === 0) return;
    let list = Array.from(files);
    // A recording attached to a meeting: offer to transcribe it onto that meeting.
    if (contextType === 'calendar_event') {
      const audio = list.filter(isAudioFile);
      if (audio.length > 0) setAudioImport({ files: audio, presetEventId: contextId });
      list = list.filter(f => !isAudioFile(f));
      if (list.length === 0) return;
    }
    try {
      await api.uploadAttachments(projectId, list, { context_type: contextType, context_id: contextId });
      loadDirectAttachments();
    } catch (err: any) {
      alert(err.message || t('timeline.uploadFailed'));
    }
  };

  const handleDeleteAttachment = async (id: number) => {
    try {
      await api.deleteAttachment(id);
      loadDirectAttachments();
      loadComments(); // refresh in case it was a comment attachment
    } catch (err: any) {
      alert(err.message || t('timeline.deleteFailed'));
    }
  };

  const getDirectAttachments = (contextType: string, contextId: number): Attachment[] => {
    return directAttachments.filter(a => a.context_type === contextType && a.context_id === contextId);
  };

  const handlePostProjectComment = async (content: string, _type: string, replyToId?: number, taggedUserIds?: number[]): Promise<number | void> => {
    const result: any = await api.postProjectComment(projectId, content, {
      tagged_user_ids: taggedUserIds,
      reply_to_id: replyToId,
      context_type: 'project',
    });
    return result?.id;
  };

  const handleEditComment = async (commentId: number, content: string) => {
    await api.editProjectComment(projectId, commentId, content);
    loadComments();
    setEditingCommentId(null);
    setEditCommentContent('');
  };

  const handleDeleteComment = async (commentId: number) => {
    await api.deleteProjectComment(projectId, commentId);
    loadComments();
  };

  const handlePostEntryComment = async (contextType: string, contextId: number, content: string, replyToId?: number, taggedUserIds?: number[]): Promise<number | void> => {
    const result: any = await api.postProjectComment(projectId, content, {
      tagged_user_ids: taggedUserIds,
      reply_to_id: replyToId,
      context_type: contextType,
      context_id: contextId,
    });
    return result?.id;
  };

  const toggleThread = (key: string) => {
    setExpandedThreadKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleRefresh = async () => {
    setSyncing(true);
    try {
      // Sync both calendar events and Speakr transcriptions in parallel
      await Promise.allSettled([
        api.triggerCalendarSync().catch(() => {}),  // silently skip if not opted in
        api.triggerSpeakrSync().catch(() => {}),     // silently skip if not configured
      ]);
      loadTimeline();
    } catch (err: any) {
      console.error('Refresh failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setFormSubject('');
    setFormDate('');
    setFormStart('');
    setFormEnd('');
    setFormLocation('');
    setFormAttendees('');
  };

  const handleSave = async () => {
    if (!formSubject || !formDate || !formStart || !formEnd) return;
    setFormSaving(true);
    try {
      const startTime = `${formDate}T${formStart}:00`;
      const endTime = `${formDate}T${formEnd}:00`;
      const attendees = formAttendees
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(name => ({ name }));

      await api.createCalendarEvent({
        project_id: projectId,
        subject: formSubject,
        start_time: startTime,
        end_time: endTime,
        is_all_day: false,
        location: formLocation || null,
        is_online_meeting: false,
        join_url: null,
        attendees: attendees.length > 0 ? attendees : null,
      });

      resetForm();
      setShowForm(false);
      loadTimeline();
    } catch (err: any) {
      alert(err.message || t('timeline.createMeetingFailed'));
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteEvent = async (id: number) => {
    try {
      await api.deleteCalendarEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (err: any) {
      alert(err.message || t('timeline.deleteMeetingFailed'));
    }
  };

  const handleDeleteProjectEvent = async (eventId: number) => {
    try {
      await api.deleteProjectEvent(projectId, eventId);
      setProjectEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (err: any) {
      alert(err.message || t('timeline.deleteEventFailed'));
    }
  };

  const handleOpenLinkDropdown = async (transcriptionId: number) => {
    try {
      const data = await api.getLinkableEvents(transcriptionId) as { events: { id: number; subject: string; start_time: string; end_time: string }[] };
      if (data.events.length === 0) return;
      setLinkDropdown({ transcriptionId, events: data.events });
    } catch { /* ignore */ }
  };

  const handleLinkTranscription = async (transcriptionId: number, eventId: number) => {
    try {
      await api.linkTranscription(transcriptionId, eventId);
      setLinkDropdown(null);
      loadTimeline();
    } catch (err: any) {
      alert(err.message || t('timeline.linkFailed'));
    }
  };

  const handleUnlinkTranscription = async (transcriptionId: number) => {
    try {
      await api.unlinkTranscription(transcriptionId);
      loadTimeline();
    } catch (err: any) {
      alert(err.message || t('timeline.unlinkFailed'));
    }
  };

  const handleDeleteRecording = async (tr: Transcription) => {
    try {
      await api.deleteTranscription(tr.id);
      setTranscriptions(prev => prev.filter(x => x.id !== tr.id));
      loadDirectAttachments();
      loadComments(); // its comments moved to the meeting or the project
    } catch (err: any) {
      alert(err.message || t('timeline.deleteRecordingFailed'));
    }
  };

  /** Bin beside a recording made in PMO, for the person who made it and admins. */
  const deleteRecordingButton = (tr: Transcription) => tr.can_delete ? (
    <button
      className="meeting-delete-btn"
      title={t('timeline.deleteRecording')}
      onClick={() => setRecordingDeleteConfirm(tr)}
    >
      <Trash2 size={12} />
    </button>
  ) : null;

  if (denied) return null;
  if (loading) return <div className="spinner" style={{ margin: '12px 0' }} />;

  const isFullMode = timelineMode === 'full';
  const entries = buildTimelineEntries(events, transcriptions, projectEvents, projectComments);
  const now = new Date().toISOString();
  const upcoming = entries.filter(e => e.date >= now);
  const past = entries.filter(e => e.date < now);

  // ── Entry-level reply thread renderer ──

  const renderEntryThread = (entry: TimelineEntry) => {
    const ctx = getEntryContextKey(entry);
    if (!ctx) return null;

    const threadKey = `${ctx.type}:${ctx.id}`;
    const comments = getThreadComments(ctx.type, ctx.id);
    const isExpanded = expandedThreads.has(threadKey);
    const isReplying = replyingTo?.contextType === ctx.type && replyingTo?.contextId === ctx.id;

    return (
      <div className="timeline-entry-thread">
        <div className="timeline-entry-thread-actions">
          {comments.length > 0 && (
            <button
              className="btn btn-ghost btn-xs timeline-entry-reply-btn"
              onClick={() => {
                toggleThread(threadKey);
                // When expanding thread, clear the standalone reply form (TaskTimeline has its own)
                if (!isExpanded && isReplying) setReplyingTo(null);
              }}
            >
              <MessageSquare size={11} /> {comments.length === 1 ? t('timeline.replyOne', { n: comments.length }) : t('timeline.replyMany', { n: comments.length })}
            </button>
          )}
          {allowComments && !isExpanded && (
            <button
              className="btn btn-ghost btn-xs timeline-entry-reply-btn"
              onClick={() => setReplyingTo(isReplying ? null : { contextType: ctx.type, contextId: ctx.id })}
            >
              <Reply size={11} /> {t('timeline.reply')}
            </button>
          )}
        </div>

        {/* Expanded thread: TaskTimeline handles all interactions (view replies, reply to replies, new top-level reply) */}
        {isExpanded && comments.length > 0 && (
          <div className="timeline-entry-comments">
            <TaskTimeline
              updates={comments}
              onPost={async (content, _type, replyToId, taggedUserIds) => {
                return handlePostEntryComment(ctx.type, ctx.id, content, replyToId, taggedUserIds);
              }}
              onEdit={handleEditComment}
              onDelete={handleDeleteComment}
              currentUserId={user!.id}
              isAdmin={isAdmin}
              projectId={projectId}
              hideHeader
              allowAttachments={allowAttachments}
              readOnly={!allowComments}
              liveKey={`project:${projectId}:comments:${ctx.type}:${ctx.id}`}
              onRefresh={() => { loadComments(); loadDirectAttachments(); }}
            />
          </div>
        )}

        {/* Standalone reply form: only when not expanded (quick new top-level reply) */}
        {isReplying && !isExpanded && (
          <div className="timeline-entry-comments">
            <InlineReplyForm
              replyTo={{ id: 0, userName: '' }}
              taggableUsers={taggableUsers}
              onPost={async (content, _type, _replyToId, taggedUserIds) => {
                const id = await handlePostEntryComment(ctx.type, ctx.id, content, undefined, taggedUserIds);
                setReplyingTo(null);
                return id;
              }}
              onCancel={() => setReplyingTo(null)}
              liveKey={`project:${projectId}:comments:${ctx.type}:${ctx.id}`}
              projectId={projectId}
              onRefresh={() => { loadComments(); loadDirectAttachments(); }}
              allowAttachments={allowAttachments}
            />
          </div>
        )}
      </div>
    );
  };

  // ── Meetings: redesigned to match transcription style ──

  const sourceLabel = (tr: Transcription) =>
    tr.source === 'recorder' ? t('timeline.fromRecorder') : tr.source === 'upload' ? t('timeline.fromUpload') : t('timeline.fromSpeakr');

  /** A transcript's button: "en cours…" while Speakr works, failed, or open it. Says where it came from when that tells something. */
  const transcriptButton = (tr: Transcription, withSource: boolean, title: string, withSpan = false) => {
    const span = withSpan ? recordingSpan(tr) : null;
    const when = span ? ` · ${span}` : '';
    return isTranscribing(tr.status) ? (
      <span className="meeting-transcript-btn meeting-transcript-btn--pending" title={sourceLabel(tr)}>
        <Mic size={12} className="timeline-icon" /> {t('timeline.transcribing')}{withSource ? ` · ${sourceLabel(tr)}` : ''}{when}
      </span>
    ) : transcriptFailed(tr) ? (
      <>
        <span className="meeting-transcript-btn meeting-transcript-btn--failed" title={sourceLabel(tr)}>
          <Mic size={12} className="timeline-icon" /> {t('timeline.transcriptFailed')}{withSource ? ` · ${sourceLabel(tr)}` : ''}{when}
        </span>
        {(tr.source === 'upload' || tr.source === 'recorder') && (
          <RedoTranscriptButton transcriptionId={tr.id} className="meeting-transcript-btn meeting-transcript-btn--active" />
        )}
      </>
    ) : (
      <button
        className="meeting-transcript-btn meeting-transcript-btn--active"
        onClick={() => setTranscriptModal({ id: tr.id, title })}
      >
        <FileText size={12} className="timeline-icon" /> {withSource ? t('timeline.transcriptFrom', { source: sourceLabel(tr) }) : t('timeline.transcript')}{when}
      </button>
    );
  };

  const renderEvent = (event: CalendarEvent, linkedTranscriptions: Transcription[] = []) => {
    const isCancelled = !!event.is_cancelled;
    const attendees = parseAttendees(event.attendees);
    const attendeeStr = formatAttendees(attendees);

    // Build info parts: attendees · location
    const infoParts: string[] = [];
    if (attendeeStr) infoParts.push(attendeeStr);
    if (event.location) infoParts.push(event.location);

    return (
      <>
        <div className="meeting-date">
          {event.is_all_day ? formatCompact(event.start_time) : (
            <>{formatCompact(event.start_time)} &middot; {formatTime(event.start_time)}&ndash;{formatTime(event.end_time)}</>
          )}
        </div>
        <div className={`meeting-subject ${isCancelled ? 'meeting-subject--cancelled' : ''}`}>
          <Calendar size={12} className="timeline-icon" />
          {event.subject}
          {isCancelled && <span className="meeting-badge--cancelled">{t('timeline.cancelled')}</span>}
        </div>
        {infoParts.length > 0 && (
          <div className="meeting-info">
            {infoParts.map((part, i) => (
              <span key={i}>{i > 0 && <span className="meeting-info-sep" />}{part}</span>
            ))}
          </div>
        )}
        {isFullMode && (
          <div className="meeting-actions">
            {linkedTranscriptions.length === 0 && (
              <span className="meeting-transcript-btn meeting-transcript-btn--disabled">
                <FileText size={12} className="timeline-icon" /> {t('timeline.noTranscript')}
              </span>
            )}
            {/* Each transcript keeps its own button: one from the recorder or an upload never hides Speakr's. */}
            {linkedTranscriptions.map(tr => (
              <span key={tr.id} className="meeting-transcript">
                {transcriptButton(tr, linkedTranscriptions.length > 1 || (!!tr.source && tr.source !== 'sync'), event.subject, true)}
                <button
                  className="meeting-link-btn meeting-attach-btn"
                  title={t('timeline.unlinkTranscription')}
                  onClick={() => handleUnlinkTranscription(tr.id)}
                >
                  <Unlink2 size={12} />
                </button>
                {deleteRecordingButton(tr)}
              </span>
            ))}
            {!!event.is_online_meeting && event.join_url && (
              <a href={event.join_url} target="_blank" rel="noopener noreferrer" className="meeting-join">
                <Video size={12} className="timeline-icon" /> {t('timeline.join')}
              </a>
            )}
            {allowAttachments && (
            <button
              className="meeting-attach-btn"
              title={t('timeline.attachFile')}
              onClick={() => {
                setUploadTarget({ contextType: 'calendar_event', contextId: event.id });
                fileInputRef.current?.click();
              }}
            >
              <Paperclip size={12} />
            </button>
            )}
            <span className="meeting-source">
              {event.source === 'manual'
                ? t('timeline.sourceManual', { name: event.created_by_user_name || t('timeline.unknown') })
                : event.source === 'dummy'
                ? t('timeline.sourceDummy')
                : 'Outlook'}
            </span>
            {canDeleteEvent(event) && (
              <button
                className="meeting-delete-btn"
                onClick={() => handleDeleteEvent(event.id)}
                title={t('timeline.deleteMeeting')}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        )}
        {(() => {
          // The meeting's files, and those of its transcripts (an uploaded recording's audio).
          const eventAttachments = [
            ...getDirectAttachments('calendar_event', event.id),
            ...linkedTranscriptions.flatMap(tr => getDirectAttachments('transcription', tr.id)),
          ];
          return eventAttachments.length > 0 ? (
            <AttachmentList
              attachments={eventAttachments}
              currentUserId={user!.id}
              isAdmin={isAdmin}
              onDelete={handleDeleteAttachment}
            />
          ) : null;
        })()}
      </>
    );
  };

  const renderTranscription = (transcription: Transcription) => {
    const parsed = parseTranscriptionTitle(transcription.title);
    const isLinkOpen = linkDropdown?.transcriptionId === transcription.id;

    return (
      <>
        <div className="meeting-date">
          {/* Recorded or uploaded in PMO: the start is exact, so the time is shown too. */}
          {transcription.source && transcription.source !== 'sync' && transcription.meeting_date ? (
            <>{formatCompact(transcription.meeting_date)} &middot; {formatTime(transcription.meeting_date)}
              {transcription.duration_seconds ? <>&ndash;{formatTime(new Date(new Date(transcription.meeting_date).getTime() + transcription.duration_seconds * 1000).toISOString())}</> : null}</>
          ) : (parsed.date || (transcription.meeting_date ? formatCompact(transcription.meeting_date) : t('timeline.noDate')))}
          {parsed.code && (
            <span className="transcript-code">{parsed.code}</span>
          )}
        </div>
        <div className="meeting-subject">
          <Mic size={12} className="timeline-icon" />
          {parsed.remainder}
        </div>
        {transcription.summary && (
          <div className="transcript-summary-inline">
            {transcription.summary.length > 120 ? transcription.summary.slice(0, 120) + '...' : transcription.summary}
          </div>
        )}
        <div className="meeting-actions">
          {transcription.duration_seconds && (
            <span style={{ color: 'var(--ink-3)' }}>{formatDuration(transcription.duration_seconds)}</span>
          )}
          {isTranscribing(transcription.status) || transcriptFailed(transcription)
            ? transcriptButton(transcription, false, parsed.remainder) : (
          <button
            className="meeting-transcript-btn meeting-transcript-btn--active"
            onClick={() => setTranscriptModal({ id: transcription.id, title: parsed.remainder })}
          >
            <FileText size={12} className="timeline-icon" /> {t('timeline.viewTranscript')}
          </button>
          )}
          <div className="meeting-link-dropdown">
            <button
              className="meeting-link-btn meeting-attach-btn"
              title={t('timeline.linkToEvent')}
              onClick={() => isLinkOpen ? setLinkDropdown(null) : handleOpenLinkDropdown(transcription.id)}
            >
              <Link2 size={12} />
            </button>
            {isLinkOpen && linkDropdown.events.length > 0 && (
              <div className="meeting-link-menu">
                {linkDropdown.events.map(ev => (
                  <button
                    key={ev.id}
                    className="meeting-link-option"
                    onClick={() => handleLinkTranscription(transcription.id, ev.id)}
                  >
                    {formatCompact(ev.start_time)} {formatTime(ev.start_time)} — {ev.subject}
                  </button>
                ))}
              </div>
            )}
          </div>
          {allowAttachments && (
          <button
            className="meeting-attach-btn"
            title={t('timeline.attachFile')}
            onClick={() => {
              setUploadTarget({ contextType: 'transcription', contextId: transcription.id });
              fileInputRef.current?.click();
            }}
          >
            <Paperclip size={12} />
          </button>
          )}
          {deleteRecordingButton(transcription)}
          <span className="meeting-source">{transcription.source === 'recorder' ? t('timeline.recordedInPmo') : sourceLabel(transcription)}</span>
        </div>
        {(() => {
          const tAttachments = getDirectAttachments('transcription', transcription.id);
          return tAttachments.length > 0 ? (
            <AttachmentList
              attachments={tAttachments}
              currentUserId={user!.id}
              isAdmin={isAdmin}
              onDelete={handleDeleteAttachment}
            />
          ) : null;
        })()}
      </>
    );
  };

  /** Get navigation href for a project event, if applicable */
  const getStatusEventHref = (pe: ProjectEvent): string | null => {
    const d = pe.details || {};
    if (TASK_EVENT_TYPES.includes(pe.event_type) && d.task_id && pe.event_type !== 'task_deleted') {
      return `#tasks?task=${d.task_id}`;
    }
    return null;
  };

  // An automatic entry (stage, edit, team, task, document, milestone): an icon
  // on the activity line, what was done, then who and when.
  const renderStatusEvent = (pe: ProjectEvent) => {
    const href = getStatusEventHref(pe);
    const desc = formatProjectEventDescription(pe, t);
    const kind = activityKind(pe.event_type);
    const Icon = ACTIVITY_ICON[kind];

    return (
      <div className={`tl-act tl-act--${kind}`}>
        <span className="tl-act-icon" aria-hidden><Icon size={12} /></span>
        <div className="tl-act-body">
          {href
            ? <a href={href} className="tl-act-text tl-act-text--link">{desc}</a>
            : <span className="tl-act-text">{desc}</span>}
          <span className="tl-act-meta">{pe.actor_name} · {formatWhen(pe.created_at, lang)}</span>
        </div>
        <span className="tl-act-tools">
          {restorable.has(pe.id) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setRestoreConfirm(pe)} title={t('timeline.restoreMilestoneHelp')}>
              <RotateCcw size={11} /> {t('timeline.restoreMilestone')}
            </button>
          )}
          {canDeleteProjectEvent(pe) && (
            <button
              className="meeting-delete-btn"
              onClick={() => handleDeleteProjectEvent(pe.id)}
              title={t('timeline.deleteEvent')}
            >
              <Trash2 size={11} />
            </button>
          )}
        </span>
      </div>
    );
  };

  // ── Comment entry in timeline ──

  const renderComment = (comment: TaskUpdate) => {
    const replies = projectCommentReplies.get(comment.id) || [];
    const isEditing = editingCommentId === comment.id;
    const isOwn = comment.user_id === user?.id;

    return (
      <>
        <span className="tl-avatar" aria-hidden>{initials(comment.user_name || '')}</span>
        <div className="timeline-comment-header">
          <span className="task-update-author">{comment.user_name}</span>
          <span className="task-update-time">{formatWhen(comment.created_at, lang)}</span>
          {allowComments && (isOwn || isAdmin) && !isEditing && (
            <span className="task-update-actions">
              {isOwn && (
                <button className="btn-icon" title={t('common.edit')} onClick={() => { setEditingCommentId(comment.id); setEditCommentContent(comment.content); }}>
                  <Pencil size={12} />
                </button>
              )}
              <button className="btn-icon" title={t('common.delete')} onClick={() => setCommentDeleteConfirm(comment.id)}>
                <Trash2 size={12} />
              </button>
            </span>
          )}
        </div>
        {isEditing ? (
          <div className="task-update-edit">
            <AutoTextarea
              value={editCommentContent}
              onChange={(e) => setEditCommentContent(e.target.value)}
              placeholder={t('timeline.editCommentPlaceholder')}
            />
            <div style={{ display: 'flex', gap: 'var(--s4)', marginTop: 'var(--s4)' }}>
              <button className="btn btn-accent btn-sm" onClick={() => handleEditComment(comment.id, editCommentContent)} disabled={!editCommentContent.trim()}>
                <Check size={12} /> {t('common.save')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingCommentId(null)}>
                <X size={12} /> {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="task-update-content">{renderCommentContent(comment.content)}</div>
        )}
        {comment.attachments && comment.attachments.length > 0 && (
          <AttachmentList
            attachments={comment.attachments}
            currentUserId={user!.id}
            isAdmin={isAdmin}
            onDelete={handleDeleteAttachment}
          />
        )}

        {/* Inline replies for this comment */}
        {replies.length > 0 && (
          <div className="timeline-entry-comments" style={{ marginTop: 'var(--s4)' }}>
            {replies.map(r => (
              <div key={r.id} className="task-update task-update--nested" style={{ paddingLeft: 'var(--s16)' }}>
                <div className="task-update-header">
                  <span className="task-update-author">{r.user_name}</span>
                  <span className="task-update-time">{formatDateTime(r.created_at)}</span>
                  {allowComments && (r.user_id === user?.id || isAdmin) && (
                    <span className="task-update-actions">
                      {r.user_id === user?.id && (
                        <button className="btn-icon" title={t('common.edit')} onClick={() => { setEditingCommentId(r.id); setEditCommentContent(r.content); }}>
                          <Pencil size={12} />
                        </button>
                      )}
                      <button className="btn-icon" title={t('common.delete')} onClick={() => setCommentDeleteConfirm(r.id)}>
                        <Trash2 size={12} />
                      </button>
                    </span>
                  )}
                </div>
                {editingCommentId === r.id ? (
                  <div className="task-update-edit">
                    <AutoTextarea
                      value={editCommentContent}
                      onChange={(e) => setEditCommentContent(e.target.value)}
                      placeholder={t('timeline.editReplyPlaceholder')}
                    />
                    <div style={{ display: 'flex', gap: 'var(--s4)', marginTop: 'var(--s4)' }}>
                      <button className="btn btn-accent btn-sm" onClick={() => handleEditComment(r.id, editCommentContent)} disabled={!editCommentContent.trim()}>
                        <Check size={12} /> {t('common.save')}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingCommentId(null)}>
                        <X size={12} /> {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="task-update-content">{renderCommentContent(r.content)}</div>
                )}
                {r.attachments && r.attachments.length > 0 && (
                  <AttachmentList
                    attachments={r.attachments}
                    currentUserId={user!.id}
                    isAdmin={isAdmin}
                    onDelete={handleDeleteAttachment}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderEntry = (entry: TimelineEntry) => {
    const key = entry.type === 'event' ? `e-${entry.event!.id}` :
                entry.type === 'transcription' ? `t-${entry.transcription!.id}` :
                entry.type === 'setup_change' ? `s-${entry.projectEvent!.id}` :
                entry.type === 'task_event' ? `te-${entry.projectEvent!.id}` :
                entry.type === 'comment' ? `cmt-${entry.comment!.id}` :
                `c-${entry.event!.id}`;

    const hasReplies = entryHasReplies(entry);

    return (
      <div key={key} className={`meeting-item timeline-entry--${entry.type}`} data-live-anchor={`tl-${key}`}>
        {hasReplies && <span className="timeline-activity-dot" />}
        {entry.type === 'event' && renderEvent(entry.event!)}
        {entry.type === 'combined' && renderEvent(entry.event!, entry.transcriptions ?? (entry.transcription ? [entry.transcription] : []))}
        {entry.type === 'transcription' && renderTranscription(entry.transcription!)}
        {(entry.type === 'setup_change' || entry.type === 'task_event') && renderStatusEvent(entry.projectEvent!)}
        {entry.type === 'comment' && renderComment(entry.comment!)}
        {isFullMode && entry.type !== 'comment' && renderEntryThread(entry)}
      </div>
    );
  };

  const isCollapsed = !expanded && overflows;

  return (
    <div ref={rootRef} className={`detail-section${compact ? ' timeline--compact' : ''}`}>
      <div className="meeting-header">
        <div className="detail-label">
          <Calendar size={14} className="timeline-icon" />
          {t('timeline.heading', { n: entries.length })}
          {!isFullMode && <span className="badge" style={{ marginLeft: 6 }}>{t('timeline.limitedView')}</span>}
        </div>
        {isFullMode && (
          <div style={{ display: 'flex', gap: 'var(--s4)' }}>
            <button
              className="btn btn-ghost btn-xs"
              onClick={handleRefresh}
              disabled={syncing}
              title={t('timeline.refreshTitle')}
            >
              <RefreshCw size={12} className={`timeline-icon${syncing ? ' spin' : ''}`} />
              {syncing ? t('timeline.syncing') : t('common.refresh')}
            </button>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => audioInputRef.current?.click()}
              title={t('timeline.importAudioTitle')}
            >
              <FileAudio size={12} className="timeline-icon" /> {t('timeline.importAudio')}
            </button>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => setShowForm(!showForm)}
              title={t('timeline.addMeeting')}
            >
              <Plus size={12} className="timeline-icon" /> {t('common.add')}
            </button>
          </div>
        )}
      </div>

      {showForm && isFullMode && (
        <div className="meeting-form">
          <input
            className="form-input"
            placeholder={t('timeline.meetingSubject')}
            value={formSubject}
            onChange={e => setFormSubject(e.target.value)}
          />
          <div className="form-grid-2">
            <input
              className="form-input"
              type="date"
              value={formDate}
              onChange={e => setFormDate(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 'var(--s4)', alignItems: 'center' }}>
              <input
                className="form-input"
                type="time"
                value={formStart}
                onChange={e => setFormStart(e.target.value)}
              />
              <span style={{ color: 'var(--ink-3)' }}>&ndash;</span>
              <input
                className="form-input"
                type="time"
                value={formEnd}
                onChange={e => setFormEnd(e.target.value)}
              />
            </div>
          </div>
          <input
            className="form-input"
            placeholder={t('timeline.locationOptional')}
            value={formLocation}
            onChange={e => setFormLocation(e.target.value)}
          />
          <input
            className="form-input"
            placeholder={t('timeline.attendeesPlaceholder')}
            value={formAttendees}
            onChange={e => setFormAttendees(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 'var(--s8)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowForm(false); resetForm(); }}>{t('common.cancel')}</button>
            <button
              className="btn btn-accent btn-sm"
              onClick={handleSave}
              disabled={formSaving || !formSubject || !formDate || !formStart || !formEnd}
            >
              {formSaving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      )}

      {/* Comment composer — top of timeline (not when comments are read-only) */}
      {allowComments && (
      <div className="project-comments-section">
        <TaskTimeline
          updates={[]}
          onPost={handlePostProjectComment}
          liveKey={`project:${projectId}:comments`}
          currentUserId={user!.id}
          isAdmin={isAdmin}
          projectId={projectId}
          hideHeader
          onRefresh={() => { loadComments(); loadDirectAttachments(); }}
          allowAttachments={allowAttachments}
        />
      </div>
      )}

      {entries.length === 0 && !showForm && (
        <div style={{ fontSize: 'var(--t-13)', color: 'var(--ink-3)', padding: 'var(--s8) 0' }}>
          {t('timeline.noActivity')}
        </div>
      )}

      <div
        ref={listRef}
        className={`task-updates-list${isCollapsed ? ' task-updates-list--collapsed' : ''}`}
        style={isCollapsed ? { maxHeight: collapseHeight } : undefined}
      >
        {upcoming.length > 0 && (
          <div className="meetings-group">
            <div className="meetings-group-label">{t('timeline.upcoming')}</div>
            {upcoming.map((entry) => renderEntry(entry))}
          </div>
        )}
        {past.length > 0 && (
          <div className="meetings-group">
            <div className="meetings-group-label">{t('timeline.past')}</div>
            {past.map((entry) => renderEntry(entry))}
          </div>
        )}
      </div>

      {isCollapsed && (
        <button className="task-updates-expand" onClick={() => setExpanded(true)}>
          <ChevronDown size={16} />
          <span>{entries.length} entries</span>
        </button>
      )}

      {transcriptModal && (
        <TranscriptModal
          transcriptionId={transcriptModal.id}
          title={transcriptModal.title}
          onClose={() => setTranscriptModal(null)}
        />
      )}

      <ConfirmModal
        open={commentDeleteConfirm !== null}
        title={t('taskTimeline.deleteTitle')}
        message={t('taskTimeline.deleteMessage')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={async () => {
          if (commentDeleteConfirm !== null) await handleDeleteComment(commentDeleteConfirm);
          setCommentDeleteConfirm(null);
        }}
        onCancel={() => setCommentDeleteConfirm(null)}
      />
      <ConfirmModal
        open={recordingDeleteConfirm !== null}
        title={t('timeline.deleteRecordingTitle')}
        message={recordingDeleteConfirm ? [
          recordingDeleteConfirm.meeting_date
            ? t('timeline.deleteRecordingWhich', { date: formatCompact(recordingDeleteConfirm.meeting_date), span: recordingSpan(recordingDeleteConfirm) || '' })
            : null,
          recordingDeleteConfirm.calendar_event_id ? t('timeline.deleteRecordingMessage') : t('timeline.deleteRecordingMessageNoMeeting'),
        ].filter(Boolean).join(' ') : ''}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={async () => {
          const tr = recordingDeleteConfirm;
          setRecordingDeleteConfirm(null);
          if (tr) await handleDeleteRecording(tr);
        }}
        onCancel={() => setRecordingDeleteConfirm(null)}
      />
      <ConfirmModal
        open={restoreConfirm !== null}
        title={t('timeline.restoreMilestoneTitle')}
        message={restoreConfirm?.details?.archived_moves?.length
          ? t('timeline.restoreMilestoneMessageTasks', { name: restoreConfirm.details.milestone_name || '', n: restoreConfirm.details.archived_moves.length })
          : t('timeline.restoreMilestoneMessage', { name: restoreConfirm?.details?.milestone_name || '' })}
        confirmLabel={t('timeline.restoreMilestone')}
        onConfirm={async () => {
          const pe = restoreConfirm;
          setRestoreConfirm(null);
          if (!pe?.details?.milestone_id) return;
          try {
            await vjtApi.restoreMilestone(projectId, pe.details!.milestone_id);
            loadComments();
            loadTimeline();
            onChanged?.();
          } catch (err: any) {
            alert(err.message);
          }
        }}
        onCancel={() => setRestoreConfirm(null)}
      />

      {/* Hidden file input for direct uploads */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        accept={`.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,${AUDIO_ACCEPT}`}
        onChange={(e) => {
          if (e.target.files && uploadTarget) {
            handleDirectUpload(e.target.files, uploadTarget.contextType, uploadTarget.contextId);
            setUploadTarget(null);
          }
          e.target.value = '';
        }}
      />
      {/* A meeting recorded on a phone or voice recorder, to transcribe into the Chronologie. */}
      <input
        ref={audioInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        accept={AUDIO_ACCEPT}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []).filter(isAudioFile);
          if (files.length > 0) setAudioImport({ files, presetEventId: null });
          e.target.value = '';
        }}
      />
      {audioImport && audioImport.files.length > 0 && (
        <AudioImportDialog
          key={`${audioImport.files[0].name}-${audioImport.files.length}`}
          file={audioImport.files[0]}
          target={{ type: 'project', id: projectId }}
          defaultKind="meeting"
          presetEventId={audioImport.presetEventId}
          attachPlain={(f) => audioImport.presetEventId
            ? api.uploadAttachments(projectId, [f], { context_type: 'calendar_event', context_id: audioImport.presetEventId })
            : docApi.upload({ type: 'project', id: projectId }, [f])}
          onCancel={() => setAudioImport(a => (a && a.files.length > 1 ? { ...a, files: a.files.slice(1) } : null))}
          onDone={() => {
            setAudioImport(a => (a && a.files.length > 1 ? { ...a, files: a.files.slice(1) } : null));
            loadTimeline();
            loadDirectAttachments();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}
